import { CacheFunction } from "@pdfking/compatibility"
import {
  type PdfiumApi,
  PdfiumExports as E,
  type PdfiumFunctions,
  type PdfiumTypeMap,
  type Pointer,
} from "@pdfking/pdfium-wrap"
import type {
  Color,
  Degree,
  Distance,
  Media,
  Page,
  PDFPlotFunction,
  Pixel,
  PlotMedia,
  PlotPath,
  PlotRect,
  PlotShape,
  PlotText,
  PlottingContext,
  Point,
  Rectangle,
  Scale,
  SpatialRectangle,
} from "@pdfking/renderer-api"
import {
  ComputeScale,
  CreateColorConvert,
  CreateContext,
  HorizontalAlign,
  HslToRgb,
  PlotFunction,
  Rotate,
  ToRadians,
  VerticalAlign,
} from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"
import { FullMethodDispatcher, Identity } from "@pdfking/tfunc"

import { CloseDocument } from "../functions/CloseDocument"
import { GetBoxes } from "../functions/GetBoxes"
import { ReadDocument } from "../functions/ReadDocument"
import { SaveDocument } from "../functions/SaveDocument"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

const { abs, cos, sin, round } = Math

type PointerDoc = PdfiumTypeMap["FPDF_DOCUMENT"]
type PointerPage = PdfiumTypeMap["FPDF_PAGE"]
type PointerPageObject = PdfiumTypeMap["FPDF_PAGEOBJECT"]
type PointerXObject = PdfiumTypeMap["FPDF_XOBJECT"]

type PageBlob = {
  readonly xobject: PointerXObject
  readonly srcPage: PointerPage
  readonly scale: Scale
} & Media<Pixel>

type CurrentPlottingContext = {
  readonly api: PdfiumApi
  readonly doc: PointerDoc
  readonly page: PointerPage
  readonly scale_bleed: Pixel
  readonly stroke_width: Pixel
  readonly get_xy: (point: Point<Distance>) => Point<Pixel>
  readonly get_measures: (
    position: SpatialRectangle<Distance>
  ) => SpatialRectangle<Pixel>
  readonly get_color: (id: string) => Color | undefined
  readonly get_page: (blob_id: string) => Promise<PageBlob | undefined>
  readonly cleanup: () => Promise<void>
}

// Encode string as UTF-16LE for pdfium text
const encodeUtf16Le = (
  { writeBytes }: PdfiumApi,
  str: string
): { pointer: Pointer; free: () => void } => {
  const utf16 = new Uint16Array(str.length + 1)
  for (let i = 0; i < str.length; i++) {
    utf16[i] = str.charCodeAt(i)
  }
  utf16[str.length] = 0
  const bytes = new Uint8Array(utf16.buffer)
  return writeBytes(bytes)
}

const to255 = (v: number) => round(v * 255)

const CreateColorSetter = (rgbFn: PdfiumFunctions, cmykFn: PdfiumFunctions) =>
  CreateColorConvert<(api: PdfiumApi, obj: PointerPageObject) => Promise<void>>(
    ({ r, g, b, a }) =>
      async ({ call }, obj) => {
        await call(rgbFn, obj, to255(r), to255(g), to255(b), to255(a))
      },
    ({ c, m, y, k, a }) =>
      async ({ call }, obj) => {
        await call(
          cmykFn,
          obj,
          to255(c),
          to255(m),
          to255(y),
          to255(k),
          to255(a)
        )
      },
    ({ h, s, l, a }) =>
      async ({ call }, obj) => {
        const { r, g, b } = HslToRgb({ h, s, l, a })
        await call(rgbFn, obj, to255(r), to255(g), to255(b), to255(a))
      }
  )

const setFillColor = CreateColorSetter(
  E.FPDFPageObj_SetFillColor,
  E.PDFKING_PageObj_SetFillColorCMYK
)

const setStrokeColor = CreateColorSetter(
  E.FPDFPageObj_SetStrokeColor,
  E.PDFKING_PageObj_SetStrokeColorCMYK
)

const CreateCurrentPlottingContext = async (
  api: PdfiumApi,
  context: Required<PlottingContext>,
  drawing: Page,
  doc: PointerDoc,
  pageIndex: number
): Promise<CurrentPlottingContext> => {
  const { safeCall, call } = api
  const to_px = (value: Distance): Pixel =>
    (context.unit_to_px * value) as Pixel

  const get_size = ({
    width,
    height,
  }: Rectangle<Distance>): Rectangle<Pixel> => ({
    width: to_px(width),
    height: to_px(height),
  })

  const { width, height } = get_size(drawing)
  const bleed = to_px(drawing.bleed ?? context.page_bleed)

  const get_xy = ({ x: px, y: py }: Point<Distance>): Point<Pixel> => ({
    x: (bleed + to_px(px)) as Pixel,
    y: (bleed - to_px(py) + height) as Pixel,
  })

  const get_measures = (
    r: SpatialRectangle<Distance>
  ): SpatialRectangle<Pixel> => {
    const { x, y } = get_xy(r)
    const { width, height } = get_size(r)
    return {
      x,
      y: (y - height) as Pixel,
      width,
      height,
    }
  }

  const get_color = (id: string): Color => {
    const color = context.color_map[id]
    if (!color) throw new Error(`Color "${id}" not found in color_map`)
    return color
  }

  // Cleanup functions to run after saving
  const cleanup_functions: Array<() => Promise<void>> = []

  // Uncached get_page - loads PDF, creates XObject, defers closing source
  const get_page = async (blob_id: string): Promise<PageBlob | undefined> => {
    const promise = context.file_map[blob_id]
    if (!promise) return undefined

    const blob = await promise
    const reader = await ReadDocument(api, {
      stream: [blob],
      size: blob.size as Bytes,
      type: blob.type,
    })

    const pageCount = await call(E.FPDF_GetPageCount, reader.doc_pointer)
    if (pageCount !== 1) {
      await CloseDocument(api, reader)
      throw new Error("PDF must have exactly one page")
    }

    const srcPage = await safeCall(E.FPDF_LoadPage, reader.doc_pointer, 0)
    const boxes = await GetBoxes(api, srcPage)

    // Create XObject from source page
    const xobject = await safeCall(
      E.FPDF_NewXObjectFromPage,
      doc,
      reader.doc_pointer,
      0
    )

    // Copy transparency group from source page to XObject if present
    // Returns 1 if transparency group exists, 0 otherwise
    await call(E.PDFKING_CopyTransparencyGroup, srcPage, xobject)

    // DEFER closing source page and document - keep memory alive until dest doc is saved
    cleanup_functions.push(async () => {
      await call(E.FPDF_ClosePage, srcPage)
      await CloseDocument(api, reader)
    })

    return {
      xobject,
      srcPage,
      scale: context.scale_map[blob_id],
      ...boxes,
    }
  }

  // Cached version - same blob_id returns same XObject
  const pageWidth = width + bleed * 2
  const pageHeight = height + bleed * 2
  const page = await safeCall(
    E.FPDFPage_New,
    doc,
    pageIndex,
    pageWidth,
    pageHeight
  )

  // Set page boxes
  await call(
    E.FPDFPage_SetTrimBox,
    page,
    bleed,
    bleed,
    bleed + width,
    bleed + height
  )
  await call(E.FPDFPage_SetMediaBox, page, 0, 0, pageWidth, pageHeight)

  const cleanup = async () => {
    for (const fn of cleanup_functions) await fn()
  }

  return {
    api,
    doc,
    page,
    get_xy,
    get_page: CacheFunction(get_page, Identity),
    get_measures,
    get_color,
    stroke_width: to_px(context.stroke_width),
    scale_bleed: to_px(context.scale_bleed),
    cleanup,
  }
}

const plot_rect = async (
  { fill_color, stroke_color, position, rotation = 0 as Degree }: PlotRect,
  context: CurrentPlottingContext
): Promise<void> => {
  const {
    api,
    api: { call, safeCall },
    page,
    get_color,
    get_measures,
    stroke_width,
  } = context
  const fillColorValue = fill_color ? get_color(fill_color) : null
  const strokeColorValue = stroke_color ? get_color(stroke_color) : null

  const { x, y, width, height } = get_measures(position)

  // Calculate rotated bottom-left corner position (same as pdflib)
  const { x: coord_x, y: coord_y } = Rotate(
    (x + width / 2) as Pixel,
    (y + height / 2) as Pixel,
    x,
    y,
    rotation
  )

  // Create rect at origin, will transform to final position
  const rect = await safeCall(E.FPDFPageObj_CreateNewRect, 0, 0, width, height)

  // Transform: [cos, sin, -sin, cos, tx, ty]
  const cs = cos(ToRadians(-rotation as Degree))
  const sn = sin(ToRadians(-rotation as Degree))
  await call(E.FPDFPageObj_Transform, rect, cs, sn, -sn, cs, coord_x, coord_y)

  // Set colors and draw mode
  if (fillColorValue) {
    await setFillColor(fillColorValue)(api, rect)
  }
  if (strokeColorValue) {
    await setStrokeColor(strokeColorValue)(api, rect)
    await call(E.FPDFPageObj_SetStrokeWidth, rect, stroke_width)
  }

  await call(
    E.FPDFPath_SetDrawMode,
    rect,
    fillColorValue ? 1 : 0,
    strokeColorValue ? 1 : 0
  )
  await call(E.FPDFPage_InsertObject, page, rect)
}

const plot_text = async (
  {
    stroke_color,
    fill_color,
    position,
    rotation = 0 as Degree,
    text,
    font_size = 12,
    text_color,
    horizontal_align,
    vertical_align,
  }: PlotText,
  context: CurrentPlottingContext
): Promise<void> => {
  const {
    api,
    api: { call, safeCall, writeString, malloc, readFloats },
    doc,
    page,
    get_color,
    get_measures,
  } = context

  // Draw background rect if colors specified
  if (stroke_color || fill_color) {
    await plot_rect({
      enum: PlotFunction.PLOT_RECT,
      rotation: 0 as Degree,
      stroke_color,
      fill_color,
      position,
    }, context)
  }

  const textColor = get_color(text_color)
  if (!textColor || !text) return

  // Create text object
  const { pointer: fontNamePtr, free: freeFontName } = writeString("Helvetica")
  const textObj = await safeCall(
    E.FPDFPageObj_NewTextObj,
    doc,
    fontNamePtr,
    font_size
  )
  freeFontName()

  // Set text content
  const { pointer: textPtr, free: freeText } = encodeUtf16Le(api, text)
  await safeCall(E.FPDFText_SetText, textObj, textPtr)
  freeText()

  // Set text color
  await setFillColor(textColor)(api, textObj)

  // Get text bounds to measure
  const { pointer: boundsPtr, free: freeBounds } = malloc(16 as Bytes)
  await call(
    E.FPDFPageObj_GetBounds,
    textObj,
    boundsPtr,
    (boundsPtr + 4) as Pointer,
    (boundsPtr + 8) as Pointer,
    (boundsPtr + 12) as Pointer
  )
  const [left, bottom, right] = readFloats(boundsPtr, 4)
  freeBounds()

  const fw = right - left
  // Use font_size * ascender ratio for Helvetica (matches pdflib's heightAtSize with descender: false)
  // Helvetica ascender is ~0.718 of the em square
  const fh = font_size * 0.718

  const { x, y, width, height } = get_measures(position)
  const { x: w, y: h } = Rotate(0 as Pixel, 0 as Pixel, width, height, rotation)

  const ew =
    horizontal_align === HorizontalAlign.LEFT
      ? -(abs(w) - fw) / 2
      : horizontal_align === HorizontalAlign.RIGHT
        ? (abs(w) - fw) / 2
        : 0
  const eh =
    vertical_align === VerticalAlign.TOP
      ? (abs(h) - fh) / 2
      : vertical_align === VerticalAlign.BOTTOM
        ? -(abs(h) - fh) / 2
        : 0

  // Calculate final text position with alignment and rotation
  const { x: coord_x, y: coord_y } = Rotate(
    (x + width / 2) as Pixel,
    (y + height / 2) as Pixel,
    (x + width / 2 - fw / 2 + ew) as Pixel,
    (y + height / 2 - fh / 2 + eh) as Pixel,
    rotation
  )

  // Build transform: first translate text origin to 0,0, then rotate, then translate to final pos
  const cs = cos(ToRadians(-rotation as Degree))
  const sn = sin(ToRadians(-rotation as Degree))

  // Combined transform: R * T where T translates (-left, -bottom) and then to coord
  // M = [cos, sin, -sin, cos, tx, ty]
  const tx = coord_x - (cs * left - sn * bottom)
  const ty = coord_y - (sn * left + cs * bottom)

  await call(E.FPDFPageObj_Transform, textObj, cs, sn, -sn, cs, tx, ty)

  await call(E.FPDFPage_InsertObject, page, textObj)
}

const plot_path = async (
  { stroke_color, fill_color, points, closed }: PlotPath,
  context: CurrentPlottingContext
): Promise<void> => {
  const {
    api,
    api: { call, safeCall },
    page,
    get_color,
    get_xy,
    stroke_width,
  } = context
  const strokeColorValue = stroke_color ? get_color(stroke_color) : null
  const fillColorValue = fill_color ? get_color(fill_color) : null

  if (points.length === 0) return

  const [first, ...rest] = points.map(get_xy)

  const path = await safeCall(E.FPDFPageObj_CreateNewPath, first.x, first.y)

  for (const { x, y } of rest) {
    await call(E.FPDFPath_LineTo, path, x, y)
  }

  if (closed) {
    await call(E.FPDFPath_Close, path)
  }

  // Set colors and draw mode (matching pdflib behavior)
  const hasFill = !!fillColorValue && closed
  const hasStroke = !!strokeColorValue

  if (hasFill) {
    await setFillColor(fillColorValue)(api, path)
  }
  if (hasStroke) {
    await setStrokeColor(strokeColorValue)(api, path)
    await call(E.FPDFPageObj_SetStrokeWidth, path, stroke_width)
  }

  await call(E.FPDFPath_SetDrawMode, path, hasFill ? 1 : 0, hasStroke ? 1 : 0)
  await call(E.FPDFPage_InsertObject, page, path)
}

const plot_media = async (
  {
    page: pageId,
    crop_position,
    media_position,
    rotation = 0 as Degree,
  }: PlotMedia,
  context: CurrentPlottingContext
): Promise<void> => {
  const {
    api: { call, safeCall },
    page,
    get_page,
    get_measures,
    scale_bleed,
  } = context

  const spec = await get_page(pageId)
  if (!spec) return
  const { trim_box: inner, media_box, scale, xobject } = spec

  const { x: cx, y: cy, width: cw, height: ch } = get_measures(crop_position)
  const media = get_measures(media_position)

  const { x: scale_x, y: scale_y } = ComputeScale<Pixel>(
    {
      outer: media,
      inner,
      bleed: scale_bleed,
    },
    scale || undefined
  )

  const { x: tx, y: ty, width: tw, height: th } = inner
  const { x: mx, y: my, width: mw, height: mh } = media

  const center_x = (tx + tw / 2) as Pixel
  const center_y = (ty + th / 2) as Pixel

  // Calculate clip bounds in source page coordinates
  const { x: left, y: bottom } = Rotate(
    center_x,
    center_y,
    (center_x - (mx + mw / 2 - cx) / scale_x) as Pixel,
    (center_y - (my + mh / 2 - cy) / scale_y) as Pixel,
    -rotation as Degree
  )

  // Adjust for media_box origin - XObject content is in media_box coordinates
  const adjusted_left = (left - media_box.x) as Pixel
  const adjusted_bottom = (bottom - media_box.y) as Pixel

  // Matrix: translate(cx, cy) * rotate(-rotation) * scale(sx, sy) * translate(-adjusted_left, -adjusted_bottom)
  const cs = cos(ToRadians(-rotation as Degree))
  const sn = sin(ToRadians(-rotation as Degree))

  const a = scale_x * cs
  const b = scale_x * sn
  const c = -scale_y * sn
  const d = scale_y * cs

  const e = cx - a * adjusted_left - c * adjusted_bottom
  const f = cy - b * adjusted_left - d * adjusted_bottom

  // Create form object from cached XObject
  const formObj = await safeCall(E.FPDF_NewFormObjectFromXObject, xobject)

  // Apply transformation matrix
  await call(E.FPDFPageObj_Transform, formObj, a, b, c, d, e, f)

  // Set clip rect on the form object
  await call(E.PDFKING_PageObj_SetClipRect, formObj, cx, cy, cx + cw, cy + ch)

  // Insert form object into page
  await call(E.FPDFPage_InsertObject, page, formObj)
}

const dispatch = FullMethodDispatcher<
  [PlotShape, CurrentPlottingContext],
  Promise<void>
>({
  [PlotFunction.PLOT_TEXT]: plot_text,
  [PlotFunction.PLOT_RECT]: plot_rect,
  [PlotFunction.PLOT_MEDIA]: plot_media,
  [PlotFunction.PLOT_PATH]: plot_path,
})

export const PDFPlot: PDFPlotFunction = (pages, partial = {}) =>
  ProvisionPdfium(async function* (api) {
    const { safeCall, call } = api

    // Create doc once at the beginning
    const doc = await safeCall(E.FPDF_CreateNewDocument)
    const cleanup_functions: Array<() => Promise<void>> = []

    for (const [pageIndex, page] of pages.entries()) {
      const context = await CreateCurrentPlottingContext(
        api,
        CreateContext(partial),
        page,
        doc,
        pageIndex
      )

      for (const shape of page.shapes) {
        await dispatch(shape, context)
      }

      await safeCall(E.FPDFPage_GenerateContent, context.page)
      await call(E.FPDF_ClosePage, context.page)

      // Collect cleanup functions
      cleanup_functions.push(context.cleanup)
    }

    // Save doc once at the end
    yield* SaveDocument(api, doc)
    await call(E.FPDF_CloseDocument, doc)

    // Run all cleanup after saving the document
    for (const cleanup of cleanup_functions) {
      await cleanup()
    }
  })
